import { Modal, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { Button, ErrorBox } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { useTransferCertificateRequest } from "~/gen/api"
import type { TransferCertificateRequestRequestDto } from "~/gen/api"
import { ContactSelect } from "~/pages/SupplierPortal/TraceabilityRequests/components/ContactSelect.tsx"
import { useAuth } from "~/store/auth-context"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

interface Props {
  certificateRequestIds: string[] | undefined
  opened: boolean
  close: () => void
}
export const TransferCertificateRequestModal = ({
  certificateRequestIds,
  opened,
  close,
}: Props) => {
  const { t } = useTranslation("traceability")
  const form = useForm<{ contactId: string }>({
    initialValues: {
      contactId: "",
    },
  })

  const handleClose = (): void => {
    form.reset()
    close()
  }

  const {
    mutateAsync: transferCertificateRequest,
    isPending: isLoading,
    error,
  } = useTransferCertificateRequest()
  const { user, isAdmin } = useAuth()

  if (!user?.id) {
    return null
  }
  return (
    <Modal title={t("transferModal.title")} opened={opened} onClose={handleClose}>
      <form
        onSubmit={form.onSubmit(async (values) => {
          if (!certificateRequestIds || certificateRequestIds.length === 0) {
            return
          }

          try {
            for (const certificateRequestId of certificateRequestIds) {
              const payload: TransferCertificateRequestRequestDto = {
                certificateRequestId,
                contactId: values.contactId,
              }
              await transferCertificateRequest({ data: payload })
            }

            handleClose()
            showSuccessNotification({
              message: t("transferModal.success"),
              title: t("transferModal.success"),
            })
          } catch (error: unknown) {
            const normalizedError =
              error instanceof Error
                ? error
                : new Error("Transfer certificate request failed")

            showAxiosError({
              error: normalizedError,
              showNotification: true,
            })
          }
        })}
      >
        <Stack>
          <ErrorBox error={error} />

          <ContactSelect
            contactId={form.values.contactId || null}
            onContactSelected={(contactId) => {
              form.setFieldValue("contactId", contactId)
            }}
            userId={user?.id}
            isAdmin={isAdmin}
          />
          <Button
            type="submit"
            disabled={
              form.values.contactId.length === 0 ||
              !certificateRequestIds ||
              certificateRequestIds.length === 0
            }
            loading={isLoading}
          >
            {t("certificates.actions.transfer")}
          </Button>
        </Stack>
      </form>
    </Modal>
  )
}
